import { hasMessageSlotCode } from "@/lib/web-guard-codes";
import type { WidgetTab } from "@/lib/widget-config";
import type { WidgetRoute } from "@/lib/widget-route";

/**
 * Pure helpers extracted from `WidgetShell` — theme/logo resolution,
 * route ranking, and the route-transition motion variants. Kept out of
 * the shell component so they can be unit-tested in isolation (see
 * `widget-shell-helpers.test.ts`).
 */

/** Normalise the operator's saved theme-mode enum (BE serialises it
 *  uppercase) down to the lowercase set the widget's CSS expects. */
export function normaliseThemeMode(value: unknown): "light" | "dark" | "auto" {
  if (typeof value !== "string") return "auto";
  const v = value.toLowerCase();
  if (v === "light" || v === "dark" || v === "auto") return v;
  return "auto";
}

/**
 * Collapse the operator's per-mode `organizationLogo` down to the
 * single URL the header components render.
 *
 *   - prefer the resolved theme mode's logo
 *   - fall back to the OTHER mode's logo (one logo beats none — an
 *     operator who only uploaded a light logo still wants it shown
 *     in dark mode rather than the generic Wexio mark)
 *   - `null` when neither mode has a logo
 */
export function resolveOrgLogo(
  logo: { light: string | null; dark: string | null } | null,
  mode: "light" | "dark",
): string | null {
  if (!logo) return null;
  const primary = mode === "dark" ? logo.dark : logo.light;
  const fallback = mode === "dark" ? logo.light : logo.dark;
  return primary ?? fallback ?? null;
}

/**
 * Does this thrown BE error represent a PERMANENT rejection — one that a retry
 * can only reproduce (input validation, a 400 `BAD_USER_INPUT`: text too long,
 * bad field) rather than a transient failure (offline, a 5xx, a dropped
 * connection)? The outbox parks a permanent failure instead of hammering the
 * BE with a request that can never succeed (see `PermanentOpError`).
 *
 * Walks the Apollo/GraphQL error shape defensively: `graphQLErrors[].extensions`
 * carries the Nest `code` + nested HTTP `response.statusCode`, and a bare
 * `networkError.statusCode` covers a non-GraphQL 4xx. Anything we can't classify
 * stays transient (safer to retry a maybe-transient error than to give up on a
 * message that would have gone through).
 */
export function isPermanentBackendError(err: unknown): boolean {
  const PERMANENT_CODES = new Set([
    "BAD_USER_INPUT",
    "BAD_REQUEST",
    "GRAPHQL_VALIDATION_FAILED",
    // Block rejections — the visitor (contact) or this thread is blocked. A
    // retry can never succeed, so the queued send is DROPPED, not hammered.
    // These are DISTINCT codes (not a bare 403, which stays a retryable
    // token-expiry case) so a genuine auth blip still retries.
    "VISITOR_BLOCKED",
    "THREAD_BLOCKED",
  ]);
  // 400 bad request, 413 payload too large, 422 unprocessable — all input-shaped.
  const PERMANENT_STATUS = new Set([400, 413, 422]);
  if (!err || typeof err !== "object") return false;
  // A rejected message-slot first send (unknown slot, key mismatch): the same request fails again.
  if (hasMessageSlotCode(err)) return true;

  const codeIsPermanent = (ext: unknown): boolean => {
    if (!ext || typeof ext !== "object") return false;
    const e = ext as { code?: unknown; response?: { statusCode?: unknown } };
    if (typeof e.code === "string" && PERMANENT_CODES.has(e.code)) return true;
    const status = e.response?.statusCode;
    return typeof status === "number" && PERMANENT_STATUS.has(status);
  };

  const gqlErrors = (err as { graphQLErrors?: unknown }).graphQLErrors;
  if (Array.isArray(gqlErrors)) {
    for (const ge of gqlErrors) {
      if (
        ge &&
        typeof ge === "object" &&
        codeIsPermanent((ge as { extensions?: unknown }).extensions)
      ) {
        return true;
      }
    }
  }

  const networkError = (err as { networkError?: unknown }).networkError;
  if (networkError && typeof networkError === "object") {
    const status = (networkError as { statusCode?: unknown }).statusCode;
    if (typeof status === "number" && PERMANENT_STATUS.has(status)) return true;
  }

  return false;
}

export function defaultRouteFor(tab: WidgetTab): WidgetRoute {
  if (tab === "messages") return { kind: "messages" };
  if (tab === "help") return { kind: "help" };
  if (tab === "news") return { kind: "news" };
  if (tab === "profile") return { kind: "profile" };
  return { kind: "home" };
}

/**
 * Stable per-route ranking used to derive transition direction.
 * Higher number = "deeper" in the visitor's mental stack. Tabs are
 * 0..3 in their nav order; drill-ins of Help live at 2.5/2.75 so
 * help → category → article reads as forward, then back returns
 * through the same rank scale.
 */
export function routeRank(route: WidgetRoute): number {
  switch (route.kind) {
    case "home":
      return 0;
    case "messages":
      return 1;
    case "messages-thread":
      // Drill-in from the Messages thread list (rank 1), so it sits just
      // "deeper" — list → thread reads as forward, back returns through it.
      return 1.5;
    case "help":
      return 2;
    case "help-category":
      return 2.5;
    case "help-article":
      return 2.75;
    case "news":
      return 3;
    case "news-article":
      return 3.25;
    case "profile":
      return 4;
  }
}

export function routeKey(route: WidgetRoute): string {
  if (route.kind === "help-category") return `help-category:${route.categoryId}`;
  if (route.kind === "help-article") return `help-article:${route.slug}`;
  if (route.kind === "news-article") return `news-article:${route.id}`;
  if (route.kind === "messages-thread") {
    // Key off the STABLE thread identity, NOT chatId. A topic thread's
    // `threadKey` is minted before its first send and never changes; the anchor
    // has no threadKey → "main". Keying on chatId would flip the key when a
    // thread's chatId is lazily adopted on first send (null → real), remounting
    // the subtree mid-conversation — AnimatePresence then plays it as a
    // navigation and the just-sent bubble animates out + back in (a flicker).
    // Distinct threads still differ (`threadKey` / "main"), so real navigations
    // still remount + animate; the anchor is the only no-threadKey thread, so
    // "main" can't collide.
    return `messages-thread:${route.keyAs ?? route.threadKey ?? "main"}`;
  }
  return route.kind;
}

/**
 * Route transition variants — Day-11 rework. Goals:
 *   1. **No height jump.** Anchor exit + enter at `inset-0` and
 *      avoid layout-affecting transforms (no scale, no blur). The
 *      panel chrome stays put, only the content slides.
 *   2. **Direction-aware slide.** Forward navigation → new view
 *      slides in from the right, old slides out to the left;
 *      back-nav mirrors. Tab-swap (direction = 0) → crossfade only.
 *   3. **Crisp settle.** Tight spring (high stiffness, high damping)
 *      so the slide reads as responsive, not springy/bouncy.
 */
export const routeVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 28 : direction < 0 ? -28 : 0,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -28 : direction < 0 ? 28 : 0,
    opacity: 0,
  }),
};
